import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { API_ENDPOINTS } from '../config/api';

// =====================================================
// INTERFACES
// =====================================================

// Helper function to calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in kilometers
};

export interface ErrandType {
  id: string;
  name: string;
  description: string;
  base_price: number;
  price_per_km: number;
  estimated_duration_minutes: number;
  requires_verification: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Errand {
  id: string;
  user_id: string;
  driver_id?: string;
  courier_company_id?: string; // Added for company-assigned drivers
  errand_type_id: string;
  title: string;
  description?: string;
  pickup_location: string;
  pickup_latitude?: number;
  pickup_longitude?: number;
  dropoff_location?: string;
  dropoff_latitude?: number;
  dropoff_longitude?: number;
  estimated_distance_km?: number;
  estimated_value?: number;
  preferred_time_slot: 'now' | 'schedule';
  scheduled_time?: string;
  payment_method: 'wallet' | 'cash_on_delivery' | 'admin_escrow';
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded';
  total_cost?: number;
  service_fee?: number;
  driver_earnings?: number;
  status: 'pending' | 'accepted' | 'in_progress' | 'picked_up' | 'completed' | 'cancelled' | 'failed' | 'paid';
  recipient_contact?: string;
  recipient_pin?: string;
  special_instructions?: string;
  insurance_opted: boolean;
  insurance_amount?: number;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  is_multi_stop?: boolean;
  // Joined data
  errand_types?: ErrandType;
  profiles?: {
    full_name: string;
    phone: string;
    email: string;
  };
  driver_profiles?: {
    full_name: string;
    phone: string;
    email: string;
  };
  courier_company?: {
    id: string;
    name: string;
  };
}

export interface ErrandProgress {
  id: string;
  errand_id: string;
  driver_id: string;
  status: 'accepted' | 'in_progress' | 'picked_up' | 'completed' | 'cancelled' | 'failed';
  location_latitude?: number;
  location_longitude?: number;
  location_address?: string;
  notes?: string;
  photo_proof_url?: string;
  recipient_signature?: string;
  completed_at?: string;
  created_at: string;
}

export interface CreateErrandData {
  errandTypeId: string;
  title: string;
  description?: string;
  pickupLocation: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  dropoffLocation?: string;
  dropoffLatitude?: number;
  dropoffLongitude?: number;
  estimatedValue?: number;
  preferredTimeSlot: 'now' | 'schedule';
  scheduledTime?: string;
  paymentMethod: 'wallet' | 'cash_on_delivery' | 'admin_escrow';
  recipientContact?: string;
  specialInstructions?: string;
  insuranceOpted?: boolean;
  insuranceAmount?: number;
}

export interface UpdateErrandStatusData {
  errandId: string;
  status: 'accepted' | 'in_progress' | 'picked_up' | 'completed' | 'cancelled' | 'failed' | 'paid';
  notes?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  locationAddress?: string;
  photoProofUrl?: string;
  recipientSignature?: string;
}

// =====================================================
// MULTI-STOP ERRAND INTERFACES
// =====================================================

export interface ErrandStop {
  id?: string;
  errand_id: string;
  sequence_number: number;
  stop_type: 'pickup' | 'dropoff' | 'intermediate' | 'return';
  location_name: string;
  latitude?: number;
  longitude?: number;
  address_details?: string;
  contact_person?: string;
  contact_phone?: string;
  special_instructions?: string;
  status: 'pending' | 'arrived' | 'completed' | 'skipped' | 'failed';
  estimated_time?: number;
  actual_arrival?: string;
  actual_departure?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateMultiStopErrandData {
  errandTypeId: string;
  title: string;
  description?: string;
  stops: Omit<ErrandStop, 'id' | 'errand_id' | 'status' | 'created_at' | 'updated_at'>[];
  preferredTimeSlot: 'now' | 'schedule';
  scheduledTime?: string;
  paymentMethod: 'wallet' | 'cash_on_delivery' | 'admin_escrow';
  specialInstructions?: string;
  insuranceOpted?: boolean;
}

export interface UpdateStopStatusData {
  errandId: string;
  stopId: string;
  status: 'pending' | 'arrived' | 'completed' | 'skipped' | 'failed';
  notes?: string;
  photoUrl?: string;
  latitude?: number;
  longitude?: number;
}

export interface ErrandRoute {
  errand_id: string;
  route: ErrandStop[];
  total_estimated_distance_km: number;
  total_estimated_time_minutes: number;
  note: string;
}

// =====================================================
// EXTRA CHARGES INTERFACES
// =====================================================

export interface ExtraCharge {
  id?: string;
  errand_id: string;
  charge_type: 'transport' | 'toll' | 'parking' | 'waiting' | 'other';
  description: string;
  amount: number;
  currency?: string;
  charged_by?: string;
  approved_by?: string;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  evidence_url?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  charged_by_profile?: {
    id: string;
    full_name: string;
    avatar_url?: string;
  };
  approved_by_profile?: {
    id: string;
    full_name: string;
    avatar_url?: string;
  };
}

export interface ExtraChargesSummary {
  total_charges: number;
  approved_charges: number;
  pending_charges: number;
  rejected_charges: number;
  total_approved_amount: number;
  total_pending_amount: number;
}

export interface ErrandExtraCharges {
  errand_id: string;
  extra_charges: ExtraCharge[];
  summary: ExtraChargesSummary;
}

export interface CreateExtraChargeData {
  errandId: string;
  chargeType: ExtraCharge['charge_type'];
  description: string;
  amount: number;
  evidenceUrl?: string;
  notes?: string;
}

export interface UpdateChargeStatusData {
  chargeId: string;
  status: 'pending' | 'approved' | 'rejected' | 'paid';
  notes?: string;
}

// =====================================================
// CONTEXT INTERFACE
// =====================================================

export interface ErrandContextType {
  // State
  errandTypes: ErrandType[];
  userErrands: Errand[];
  availableErrands: Errand[];
  driverErrands: Errand[];
  loading: boolean;
  error: string | null;
  errandStops: { [errandId: string]: ErrandStop[] };
  errandRoutes: { [errandId: string]: ErrandRoute };
  errandExtraCharges: { [errandId: string]: ErrandExtraCharges };

  // Actions
  fetchErrandTypes: () => Promise<void>;
  createErrand: (data: CreateErrandData) => Promise<Errand | null>;
  fetchUserErrands: () => Promise<void>;
  fetchAvailableErrands: (userLatitude?: number, userLongitude?: number, radiusKm?: number) => Promise<void>;
  fetchDriverErrands: (userLatitude?: number, userLongitude?: number, radiusKm?: number) => Promise<void>;
  updateErrandStatus: (data: UpdateErrandStatusData) => Promise<boolean>;
  cancelErrand: (errandId: string, reason: string, otherReason?: string) => Promise<boolean>;
  submitRating: (errandId: string, driverId: string, rating: number, comment?: string) => Promise<boolean>;
  createRecurringErrand: (data: any) => Promise<boolean>;
  clearError: () => void;

  // Multi-stop errand functions
  createMultiStopErrand: (data: CreateMultiStopErrandData) => Promise<Errand | null>;
  updateStopStatus: (data: UpdateStopStatusData) => Promise<boolean>;
  getErrandRoute: (errandId: string) => Promise<ErrandRoute | null>;
  fetchErrandStops: (errandId: string) => Promise<void>;

  // Extra charges functions
  createExtraCharge: (data: CreateExtraChargeData) => Promise<ExtraCharge | null>;
  getErrandExtraCharges: (errandId: string) => Promise<ErrandExtraCharges | null>;
  updateChargeStatus: (data: UpdateChargeStatusData) => Promise<boolean>;
  approveExtraCharge: (chargeId: string) => Promise<boolean>;
}

// =====================================================
// CONTEXT CREATION
// =====================================================

const ErrandContext = createContext<ErrandContextType | undefined>(undefined);

// =====================================================
// PROVIDER COMPONENT
// =====================================================

export const ErrandProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [errandTypes, setErrandTypes] = useState<ErrandType[]>([]);
  const [userErrands, setUserErrands] = useState<Errand[]>([]);
  const [availableErrands, setAvailableErrands] = useState<Errand[]>([]);
  const [driverErrands, setDriverErrands] = useState<Errand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Multi-stop errands state
  const [errandStops, setErrandStops] = useState<{ [errandId: string]: ErrandStop[] }>({});
  const [errandRoutes, setErrandRoutes] = useState<{ [errandId: string]: ErrandRoute }>({});

  // Extra charges state
  const [errandExtraCharges, setErrandExtraCharges] = useState<{ [errandId: string]: ErrandExtraCharges }>({});

  // Fetch available errand types
  const fetchErrandTypes = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching errand types...');

      const { data, error } = await supabase
        .from('errand_types')
        .select('*')
        .eq('is_active', true)
        .order('name');

      console.log('Supabase response:', { data, error });

      if (error) throw error;
      setErrandTypes(data || []);
      console.log('Errand types set:', data || []);
    } catch (err) {
      console.error('Error fetching errand types:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch errand types');
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new errand
  const createErrand = useCallback(async (data: CreateErrandData): Promise<Errand | null> => {
    try {
      setLoading(true);
      setError(null);

      // Validate input data
      if (!data.errandTypeId) {
        throw new Error('Errand type is required');
      }
      if (!data.title) {
        throw new Error('Errand title is required');
      }
      if (!data.pickupLocation) {
        throw new Error('Pickup location is required');
      }

      // Get JWT token from localStorage since we're using JWT auth
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('User not authenticated');
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      // Log the data being sent for debugging
      const errandPayload = {
        p_user_id: userId,
        p_errand_type_id: data.errandTypeId,
        p_title: data.title,
        p_description: data.description || null,
        p_pickup_location: data.pickupLocation,
        p_pickup_lat: data.pickupLatitude || null,
        p_pickup_lng: data.pickupLongitude || null,
        p_dropoff_location: data.dropoffLocation || null,
        p_dropoff_lat: data.dropoffLatitude || null,
        p_dropoff_lng: data.dropoffLongitude || null,
        p_estimated_value: data.estimatedValue || 0,
        p_preferred_time_slot: data.preferredTimeSlot,
        p_scheduled_time: data.scheduledTime || null,
        p_payment_method: data.paymentMethod,
        p_recipient_contact: data.recipientContact || null,
        p_special_instructions: data.specialInstructions || null
      };

      console.log('Creating errand with payload:', errandPayload);

      const { data: errand, error } = await supabase
        .rpc('create_errand', errandPayload);

      console.log('Supabase RPC response:', { data: errand, error });

      if (error) {
        console.error('Supabase RPC error details:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        throw new Error(`Failed to create errand: ${error.message || 'Unknown error'}`);
      }

      console.log('Errand created successfully:', errand);

      // Refresh user errands after successful creation
      await fetchUserErrands();

      return errand;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create errand');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user's errands
  const fetchUserErrands = useCallback(async () => {
    try {
      setLoading(true);

      // Get JWT token from localStorage since we're using JWT auth
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No JWT token found - user not authenticated yet');
        return;
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      const { data, error } = await supabase
        .from('errands')
        .select(`
          *,
          errand_types (*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserErrands(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user errands');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch available errands for drivers
  const fetchAvailableErrands = useCallback(async (userLatitude?: number, userLongitude?: number, radiusKm: number = 10) => {
    try {
      setLoading(true);

      // Get JWT token from localStorage since we're using JWT auth
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No JWT token found - user not authenticated yet');
        return;
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;
      const userRole = profileData.profile?.role;
      const isDriver = profileData.profile?.driverApplicationStatus === 'approved';

      console.log('Fetching available errands for user ID:', userId);
      console.log('User role:', userRole, 'isDriver:', isDriver);
      console.log('Location filter:', { userLatitude, userLongitude, radiusKm });

      // Allow verified drivers (role = 'driver' or isDriver = true) and couriers (role = 'courier') to see available errands
      if (userRole !== 'driver' && userRole !== 'courier' && !isDriver) {
        console.log('User is not a driver or courier, returning empty errands list');
        setAvailableErrands([]);
        setLoading(false);
        return;
      }

      let query = supabase
        .from('errands')
        .select(`
          *,
          errand_types (*),
          courier_company:courier_companies (id, name)
        `)
        .eq('status', 'pending')
        .is('driver_id', null); // Only show errands not accepted by any driver

      // If location coordinates are provided, filter by distance
      if (userLatitude && userLongitude) {
        // Use PostGIS distance function if available, otherwise filter in JavaScript
        const { data, error } = await query.order('created_at', { ascending: false });

        if (error) {
          console.error('Supabase error details:', error);
          throw error;
        }

        // Filter by distance in JavaScript (since PostGIS might not be available)
        const filteredData = data?.filter(errand => {
          if (!errand.pickup_latitude || !errand.pickup_longitude) {
            return true; // Include errands without location data
          }

          const distance = calculateDistance(
            userLatitude,
            userLongitude,
            errand.pickup_latitude,
            errand.pickup_longitude
          );

          return distance <= radiusKm;
        }) || [];

        console.log(`Found ${filteredData.length} errands within ${radiusKm}km radius`);
        setAvailableErrands(filteredData);
      } else {
        // No location filter, get all pending errands
        const { data, error } = await query.order('created_at', { ascending: false });

        console.log('Available errands response:', { data, error });

        if (error) {
          console.error('Supabase error details:', error);
          throw error;
        }
        setAvailableErrands(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch available errands');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch driver's accepted errands
  const fetchDriverErrands = useCallback(async (userLatitude?: number, userLongitude?: number, radiusKm: number = 10) => {
    try {
      setLoading(true);

      // Get JWT token from localStorage since we're using JWT auth
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No JWT token found - user not authenticated yet');
        return;
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      console.log('Fetching driver errands for driver ID:', userId);
      console.log('Location filter:', { userLatitude, userLongitude, radiusKm });

      let query = supabase
        .from('errands')
        .select(`
          *,
          errand_types (*),
          courier_company:courier_companies (id, name)
        `)
        .eq('driver_id', userId)
        .in('status', ['accepted', 'in_progress', 'picked_up', 'completed', 'paid'])  // Only show non-pending errands
        .order('created_at', { ascending: false });

      // If location coordinates are provided, filter by distance
      if (userLatitude && userLongitude && radiusKm > 0) {
        const { data, error } = await query;

        if (error) {
          console.error('Supabase driver errands error details:', error);
          throw error;
        }

        // Filter by distance in JavaScript
        const filteredData = data?.filter(errand => {
          if (!errand.pickup_latitude || !errand.pickup_longitude) {
            return true; // Include errands without location data
          }

          const distance = calculateDistance(
            userLatitude,
            userLongitude,
            errand.pickup_latitude,
            errand.pickup_longitude
          );

          return distance <= radiusKm;
        }) || [];

        console.log(`Found ${filteredData.length} driver errands within ${radiusKm}km radius`);
        setDriverErrands(filteredData);
      } else {
        // No location filter, get all driver errands
        const { data, error } = await query;

        console.log('Driver errands response:', { data, error });

        if (error) {
          console.error('Supabase driver errands error details:', error);
          throw error;
        }
        setDriverErrands(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch driver errands');
    } finally {
      setLoading(false);
    }
  }, []);

  // Update errand status
  const updateErrandStatus = useCallback(async (data: UpdateErrandStatusData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Get JWT token from localStorage since we're using JWT auth
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No JWT token found - user not authenticated yet');
        return false;
      }

      // Get user profile from the token
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      // Handle different errand completion scenarios
      if (data.status === 'completed') {
        // Check if this is a cash on delivery errand
        const { data: errandData, error: errandFetchError } = await supabase
          .from('errands')
          .select('payment_method')
          .eq('id', data.errandId)
          .single();

        if (errandFetchError) {
          console.error('Error fetching errand:', errandFetchError);
          throw errandFetchError;
        }

        if (errandData.payment_method === 'cash_on_delivery') {
          // Use cash on delivery completion function
          console.log('Completing cash on delivery errand:', { errandId: data.errandId, courierId: userId });

          const { data: result, error: rpcError } = await supabase
            .rpc('complete_cash_on_delivery_errand', {
              p_errand_id: data.errandId,
              p_driver_id: userId
            });

          console.log('Cash on delivery RPC result:', { result, error: rpcError });

          if (rpcError) {
            console.error('RPC error:', rpcError);
            throw rpcError;
          }
          if (!result) {
            console.error('RPC returned false');
            throw new Error('Failed to complete cash on delivery errand');
          }

          console.log('Cash on delivery errand completed successfully');

          // Dispatch event to refresh earnings and stats
          window.dispatchEvent(new CustomEvent('errandCompleted', { detail: { errandId: data.errandId } }));
        } else {
          // Use escrow completion function for other payment methods
          console.log('Completing errand with escrow:', { errandId: data.errandId, courierId: userId });

          const { data: result, error: rpcError } = await supabase
            .rpc('complete_errand_with_escrow', {
              p_errand_id: data.errandId,
              p_courier_id: userId
            });

          console.log('Escrow RPC result:', { result, error: rpcError });

          if (rpcError) {
            console.error('RPC error:', rpcError);
            throw rpcError;
          }
          if (!result) {
            console.error('RPC returned false');
            throw new Error('Failed to complete errand');
          }

          console.log('Escrow errand completed successfully');

          // Dispatch event to refresh earnings and stats
          window.dispatchEvent(new CustomEvent('errandCompleted', { detail: { errandId: data.errandId } }));
        }
      } else if (data.status === 'paid') {
        // Handle marking cash payment as received
        console.log('Marking cash payment as received:', { errandId: data.errandId, driverId: userId });

        const { data: result, error: rpcError } = await supabase
          .rpc('mark_cash_payment_received', {
            p_errand_id: data.errandId,
            p_driver_id: userId
          });

        console.log('Cash payment RPC result:', { result, error: rpcError });

        if (rpcError) {
          console.error('RPC error:', rpcError);
          throw new Error(rpcError.message || 'Failed to mark cash payment as received');
        }
        if (!result) {
          console.error('RPC returned false');
          throw new Error('Failed to mark cash payment as received - errand not found or invalid state');
        }

        console.log('Cash payment marked as received successfully');

        // Refresh errand data to reflect payment status change
        const { error: refreshError } = await supabase
          .from('errands')
          .select('*')
          .eq('id', data.errandId)
          .single();

        if (refreshError) {
          console.error('Error refreshing errand:', refreshError);
        }

        // Note: mark_cash_payment_received RPC already creates the progress entry
        // No need to create another one here
      } else {
        // For other status updates, use the old method
        const updateData: any = {
          status: data.status,
          driver_id: data.status === 'accepted' ? userId : undefined,
          updated_at: new Date().toISOString()
        };

        // If notes contain payment confirmation, update payment status
        if (data.notes && data.notes.includes('Cash payment received')) {
          updateData.payment_status = 'completed';
        }

        const { error: errandError } = await supabase
          .from('errands')
          .update(updateData)
          .eq('id', data.errandId);

        if (errandError) throw errandError;

        // Create progress entry for non-paid statuses
        // (paid status already handled by RPC function above)
        // TypeScript narrows the type in the else block, so we need to check the string value
        const statusValue = String(data.status);
        const progressStatus = statusValue as 'accepted' | 'in_progress' | 'picked_up' | 'completed' | 'cancelled' | 'failed' | 'payment_received';
        const isCompleted = (statusValue === 'completed');

        console.log('Creating progress entry:', {
          errand_id: data.errandId,
          driver_id: userId,
          status: progressStatus
        });

        const { error: progressError } = await supabase
          .from('errand_progress')
          .insert({
            errand_id: data.errandId,
            driver_id: userId,
            status: progressStatus,
            location_latitude: data.locationLatitude,
            location_longitude: data.locationLongitude,
            location_address: data.locationAddress,
            notes: data.notes,
            photo_proof_url: data.photoProofUrl,
            recipient_signature: data.recipientSignature,
            completed_at: isCompleted ? new Date().toISOString() : undefined
          });

        if (progressError) {
          console.error('Progress error:', progressError);
          throw progressError;
        }

        console.log('Progress entry created successfully');
      }

      // Refresh errands
      console.log('Refreshing errands...');
      await Promise.all([
        fetchAvailableErrands(),
        fetchDriverErrands()
      ]);

      console.log('Errand status update completed successfully');
      return true;
    } catch (err) {
      console.error('Error in updateErrandStatus:', err);
      setError(err instanceof Error ? err.message : 'Failed to update errand status');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchAvailableErrands, fetchDriverErrands]);

  // Cancel errand with reason
  const cancelErrand = useCallback(async (errandId: string, reason: string, otherReason?: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const cancellationReason = reason === 'other' ? otherReason : reason;

      const { error } = await supabase
        .from('errands')
        .update({
          status: 'cancelled',
          cancellation_reason: cancellationReason,
          cancelled_by: user.id,
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', errandId);

      if (error) throw error;

      // Create progress entry for cancellation
      await supabase
        .from('errand_progress')
        .insert({
          errand_id: errandId,
          status: 'cancelled',
          notes: `Cancelled: ${cancellationReason}`
        });

      // Refresh user errands
      await fetchUserErrands();

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel errand');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchUserErrands]);

  // Submit rating for driver
  const submitRating = useCallback(async (errandId: string, driverId: string, rating: number, comment?: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('driver_ratings')
        .insert({
          errand_id: errandId,
          driver_id: driverId,
          customer_id: user.id,
          rating,
          comment: comment || null
        });

      if (error) {
        // If rating already exists, update it
        if (error.code === '23505') {
          const { error: updateError } = await supabase
            .from('driver_ratings')
            .update({
              rating,
              comment: comment || null,
              updated_at: new Date().toISOString()
            })
            .eq('errand_id', errandId)
            .eq('customer_id', user.id);

          if (updateError) throw updateError;
        } else {
          throw error;
        }
      }

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit rating');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create recurring errand
  const createRecurringErrand = useCallback(async (data: any): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Calculate next run date
      const now = new Date();
      let nextRunDate: Date;

      if (data.frequency === 'daily') {
        nextRunDate = new Date(now);
        nextRunDate.setDate(nextRunDate.getDate() + 1);
      } else if (data.frequency === 'weekly') {
        nextRunDate = new Date(now);
        const daysUntilNext = (data.dayOfWeek - now.getDay() + 7) % 7 || 7;
        nextRunDate.setDate(nextRunDate.getDate() + daysUntilNext);
      } else { // monthly
        nextRunDate = new Date(now.getFullYear(), now.getMonth(), data.dayOfMonth);
        if (nextRunDate <= now) {
          nextRunDate = new Date(now.getFullYear(), now.getMonth() + 1, data.dayOfMonth);
        }
      }

      const { error } = await supabase
        .from('recurring_errands')
        .insert({
          user_id: user.id,
          errand_type_id: data.errandTypeId,
          title: data.title,
          description: data.description,
          pickup_location: data.pickupLocation,
          pickup_latitude: data.pickupLatitude,
          pickup_longitude: data.pickupLongitude,
          dropoff_location: data.dropoffLocation,
          dropoff_latitude: data.dropoffLatitude,
          dropoff_longitude: data.dropoffLongitude,
          estimated_value: data.estimatedValue,
          payment_method: data.paymentMethod,
          recipient_contact: data.recipientContact,
          special_instructions: data.specialInstructions,
          frequency: data.frequency,
          day_of_week: data.dayOfWeek,
          day_of_month: data.dayOfMonth,
          time_of_day: data.timeOfDay,
          next_run_date: nextRunDate.toISOString().split('T')[0],
          is_active: true
        });

      if (error) throw error;

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create recurring errand');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // =====================================================
  // MULTI-STOP ERRAND FUNCTIONS
  // =====================================================

  // Create multi-stop errand
  const createMultiStopErrand = useCallback(async (data: CreateMultiStopErrandData): Promise<Errand | null> => {
    try {
      setLoading(true);
      setError(null);

      // Validate input data
      if (!data.errandTypeId || !data.title || !data.stops || data.stops.length < 2) {
        throw new Error('Invalid data: errand type, title, and at least 2 stops are required');
      }

      // Get JWT token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('User not authenticated');
      }

      // Get user profile
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const userId = profileData.profile.id;

      // Prepare payload for multi-stop errand API
      const payload = {
        errand_type_id: data.errandTypeId,
        title: data.title,
        description: data.description || null,
        stops: data.stops.map(stop => ({
          stop_type: stop.stop_type,
          location_name: stop.location_name,
          latitude: stop.latitude || null,
          longitude: stop.longitude || null,
          address_details: stop.address_details || null,
          contact_person: stop.contact_person || null,
          contact_phone: stop.contact_phone || null,
          special_instructions: stop.special_instructions || null
        })),
        preferred_time_slot: data.preferredTimeSlot,
        scheduled_time: data.scheduledTime || null,
        payment_method: data.paymentMethod,
        special_instructions: data.specialInstructions || null,
        insurance_opted: data.insuranceOpted || false
      };

      console.log('Creating multi-stop errand:', payload);

      const createResponse = await fetch(`${API_ENDPOINTS.BASE}/api/multi-stop-errands`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || 'Failed to create multi-stop errand');
      }

      const result = await createResponse.json();
      console.log('Multi-stop errand created:', result);

      // Refresh user errands
      await fetchUserErrands();

      return result.errand;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create multi-stop errand');
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchUserErrands]);

  // Update stop status
  const updateStopStatus = useCallback(async (data: UpdateStopStatusData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('User not authenticated');
      }

      const payload = {
        status: data.status,
        notes: data.notes || null,
        photo_url: data.photoUrl || null,
        latitude: data.latitude || null,
        longitude: data.longitude || null
      };

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/multi-stop-errands/${data.errandId}/stops/${data.stopId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update stop status');
      }

      // Refresh errand stops
      await fetchErrandStops(data.errandId);

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update stop status');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get errand route
  const getErrandRoute = useCallback(async (errandId: string): Promise<ErrandRoute | null> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/multi-stop-errands/${errandId}/route`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get errand route');
      }

      const routeData = await response.json();
      setErrandRoutes(prev => ({ ...prev, [errandId]: routeData }));

      return routeData;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get errand route');
      return null;
    }
  }, []);

  // Fetch errand stops
  const fetchErrandStops = useCallback(async (errandId: string): Promise<void> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/multi-stop-errands/${errandId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setErrandStops(prev => ({ ...prev, [errandId]: data.errand.stops || [] }));
      }
    } catch (err) {
      console.error('Error fetching errand stops:', err);
    }
  }, []);

  // =====================================================
  // EXTRA CHARGES FUNCTIONS
  // =====================================================

  // Create extra charge
  const createExtraCharge = useCallback(async (data: CreateExtraChargeData): Promise<ExtraCharge | null> => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('User not authenticated');
      }

      const payload = {
        errand_id: data.errandId,
        charge_type: data.chargeType,
        description: data.description,
        amount: data.amount,
        evidence_url: data.evidenceUrl || null,
        notes: data.notes || null
      };

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/extra-charges`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create extra charge');
      }

      const result = await response.json();

      // Refresh extra charges for this errand
      await getErrandExtraCharges(data.errandId);

      return result.extra_charge;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create extra charge');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get errand extra charges
  const getErrandExtraCharges = useCallback(async (errandId: string): Promise<ErrandExtraCharges | null> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/extra-charges/errand/${errandId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get extra charges');
      }

      const chargesData = await response.json();
      setErrandExtraCharges(prev => ({ ...prev, [errandId]: chargesData }));

      return chargesData;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get extra charges');
      return null;
    }
  }, []);

  // Update charge status
  const updateChargeStatus = useCallback(async (data: UpdateChargeStatusData): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('User not authenticated');
      }

      const payload = {
        status: data.status,
        notes: data.notes || null
      };

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/extra-charges/${data.chargeId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update charge status');
      }

      // Refresh extra charges for all errands that might be affected
      // Note: In a real app, you'd want to know which errand this charge belongs to
      // For now, we'll refresh all cached extra charges
      Object.keys(errandExtraCharges).forEach(errandId => {
        getErrandExtraCharges(errandId);
      });

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update charge status');
      return false;
    } finally {
      setLoading(false);
    }
  }, [errandExtraCharges, getErrandExtraCharges]);

  // Approve extra charge (customer approval)
  const approveExtraCharge = useCallback(async (chargeId: string): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/extra-charges/${chargeId}/customer-approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to approve extra charge');
      }

      // Refresh extra charges for all errands that might be affected
      Object.keys(errandExtraCharges).forEach(errandId => {
        getErrandExtraCharges(errandId);
      });

      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve extra charge');
      return false;
    } finally {
      setLoading(false);
    }
  }, [errandExtraCharges, getErrandExtraCharges]);

  // Load errand types on mount
  useEffect(() => {
    fetchErrandTypes();
  }, [fetchErrandTypes]);

  const value: ErrandContextType = {
    errandTypes,
    userErrands,
    availableErrands,
    driverErrands,
    loading,
    error,
    errandStops,
    errandRoutes,
    errandExtraCharges,
    fetchErrandTypes,
    createErrand,
    fetchUserErrands,
    fetchAvailableErrands,
    fetchDriverErrands,
    updateErrandStatus,
    cancelErrand,
    submitRating,
    createRecurringErrand,
    clearError,
    createMultiStopErrand,
    updateStopStatus,
    getErrandRoute,
    fetchErrandStops,
    createExtraCharge,
    getErrandExtraCharges,
    updateChargeStatus,
    approveExtraCharge
  };

  return (
    <ErrandContext.Provider value={value}>
      {children}
    </ErrandContext.Provider>
  );
};

// =====================================================
// HOOK
// =====================================================

export const useErrand = (): ErrandContextType => {
  const context = useContext(ErrandContext);
  if (context === undefined) {
    throw new Error('useErrand must be used within an ErrandProvider');
  }
  return context;
};
